import { gql, useQuery } from '@apollo/client';
import {
  Box,
  HStack,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@chakra-ui/layout';
import NextLink from 'next/link';
import * as React from 'react';

import { sponsor_content_clicked, sponsor_page_viewed } from '@/analytics';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { Card } from '@/imports/ui/chakra/components/Card';

import { FeaturedSponsor_GetFeaturedSponsor } from './graphql-types/FeaturedSponsor_GetFeaturedSponsor';

import { FeaturedSponsorButton } from './components/FeaturedSponsorButton';

const GET_FEATURED_SPONSOR = gql`
  query FeaturedSponsor_GetFeaturedSponsor {
    getFeaturedSponsor {
      result {
        _id
        name
        ...OrganizationProfileImage_OrganizationFragment
        ...FeaturedSponsorButton_SponsorFragment
      }
    }
  }
  ${FeaturedSponsorButton.sponsorFragment}
  ${OrganizationProfileImage.organizationFragment}
`;

export const FeaturedSponsor: React.FC = () => {
  const query =
    useQuery<FeaturedSponsor_GetFeaturedSponsor>(GET_FEATURED_SPONSOR);

  const featuredSponsor = query.data?.getFeaturedSponsor.result;
  const featuredSponsorId = featuredSponsor?._id;
  const featuredSponsorName = featuredSponsor?.name;
  const featuredSponsorTier =
    featuredSponsor?.sponsorship?.currentTier?.analyticsTierType;

  React.useEffect(() => {
    if (!featuredSponsorId) return;

    sponsor_page_viewed({
      sponsor_id: featuredSponsorId,
      sponsor_name: featuredSponsorName,
      sponsor_tier: featuredSponsorTier,
      sponsor_interaction_source: 'featured_sponsor',
      location_organization_id: featuredSponsorId,
    });
  }, [featuredSponsorId, featuredSponsorName, featuredSponsorTier]);

  if (!featuredSponsor) {
    return null;
  }

  return (
    <Card width="full">
      <Stack spacing="2" p="4" w="full">
        <Text w="full" color="gray.600" textStyle="compactStrong" px="2">
          FEATURED SPONSOR
        </Text>
        <LinkBox w="full" p="2" _hover={{ bg: 'gray.100' }} borderRadius="xl">
          <HStack spacing="2" alignItems="center" w="full">
            <OrganizationProfileImage
              h={20}
              w={20}
              organization={featuredSponsor}
            />
            <Box flex="1" minWidth="0">
              <LinkOverlay
                as={NextLink}
                href={`/companies/${featuredSponsor._id}`}
                onClick={() => {
                  sponsor_content_clicked({
                    sponsor_id: featuredSponsor._id,
                    sponsor_name: featuredSponsor.name,
                    sponsor_tier:
                      featuredSponsor.sponsorship?.currentTier
                        ?.analyticsTierType,
                    sponsor_content_type: 'featured_placement',
                    sponsor_content_id: featuredSponsor._id,
                    sponsor_interaction_source: 'featured_sponsor',
                    location_organization_id: featuredSponsor._id,
                  });
                }}
              >
                <Text textStyle="xlBodyStrong" noOfLines={2}>
                  {featuredSponsor.name}
                </Text>
              </LinkOverlay>
            </Box>
          </HStack>
        </LinkBox>
        <FeaturedSponsorButton featuredSponsor={featuredSponsor} />
      </Stack>
    </Card>
  );
};
